import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomButton from "components/common/CustomButton";
import {
  connectionLabel,
  useGetConnectionQuery,
} from "features/integrationsBeta/connections";
import {
  IntegrationStepItem,
  ReadinessIssue,
  StepCatalogEntry,
  useDeleteStepMutation,
} from "features/integrationsBeta/integrationsBeta";
import { useGetIntegrationStepStagesQuery } from "features/integrationsBeta/platformEnums";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useMemo, useState } from "react";
import JsonSchemaForm from "./JsonSchemaForm";
import type { Schema } from "./jsonSchemaUtils";
import PayloadView, { PendingDot } from "./PayloadView";
import { changedPathSet, diffConfigTree } from "./pending/configDiff";
import { usePendingChanges } from "./pending/PendingChangesContext";
import StageChip from "./StageChip";

type Json = Record<string, unknown>;

interface Props {
  step: IntegrationStepItem;
  integrationId: number;
  catalogEntry: StepCatalogEntry | null;
  stageLabel: string;
  issues?: ReadinessIssue[];
  onNavigateToConnection?: (connectionId: number) => void;
}

/**
 * The step's connection is fixed when the step is added (driven by the
 * step's declared requirement) — the chip only navigates to the Connections
 * tab. Rebinding is deliberately not offered here: a wrong binding is fixed
 * by deleting and re-adding the step, which avoids half-configured duplicate
 * connections.
 */
function ConnectionChipRow({
  step,
  catalogEntry,
  onNavigate,
}: {
  step: IntegrationStepItem;
  catalogEntry: StepCatalogEntry | null;
  onNavigate?: (connectionId: number) => void;
}) {
  const { data: connection } = useGetConnectionQuery(step.connection ?? 0, {
    skip: step.connection == null,
  });
  const { getPending } = usePendingChanges();
  // Staged rename overlays the server value so the chip matches the card.
  const pendingName =
    step.connection != null
      ? getPending("connection", step.connection, "name")
      : undefined;
  const connectionName = connection
    ? connectionLabel({
        ...connection,
        name: (pendingName?.newValue as string | undefined) ?? connection.name,
      })
    : `#${step.connection}`;
  // null = declared "no connection"; undefined = no metadata (old catalog).
  const requirement = catalogEntry?.connection_requirement;

  if (step.connection == null && requirement === null) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
      {step.connection != null ? (
        <Chip
          size="small"
          variant="outlined"
          label={
            <>
              Connection: {connectionName}
              {pendingName && <PendingDot />}
            </>
          }
          onClick={
            onNavigate ? () => onNavigate(step.connection as number) : undefined
          }
        />
      ) : (
        <Typography
          variant="caption"
          color={requirement ? "warning.main" : "text.secondary"}
        >
          {requirement
            ? `No connection bound — requires ${
                requirement.provider_slug ?? "any provider"
              } (${requirement.connection_type}). Delete the step and add it again to bind one.`
            : "No connection."}
        </Typography>
      )}
    </Box>
  );
}

export default function StepConfigCard({
  step,
  integrationId,
  catalogEntry,
  stageLabel,
  issues = [],
  onNavigateToConnection,
}: Props) {
  const dispatch = useAppDispatch();
  const { canEdit, canManageStructure } = usePlatformRole();
  const { stage, getPending } = usePendingChanges();
  const [deleteStep, { isLoading: deleting }] = useDeleteStepMutation();
  const { data: stages } = useGetIntegrationStepStagesQuery();

  const schema = catalogEntry?.config_schema ?? null;

  const [editOpen, setEditOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState(step.name);
  const [stageDraft, setStageDraft] = useState(step.stage);
  const [orderDraft, setOrderDraft] = useState<string>(
    String(step.stage_order),
  );
  const [draft, setDraft] = useState<Json>({});
  const [rawDraft, setRawDraft] = useState("");
  const [rawError, setRawError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const entityLabel = `Step "${step.name}"`;
  const stagedName =
    (getPending("step", step.id, "name")?.newValue as string | undefined) ??
    step.name;
  const pendingConfig = getPending("step", step.id, "config");
  const stagedConfig =
    (pendingConfig?.newValue as Json | undefined) ?? (step.config as Json) ?? {};
  // Which config fields carry a staged edit -> warning dots on their rows.
  const changedConfigPaths = useMemo(
    () =>
      pendingConfig
        ? changedPathSet(
            diffConfigTree(
              pendingConfig.oldValue,
              pendingConfig.newValue,
              (pendingConfig.schema ?? null) as Schema | null,
            ),
          )
        : undefined,
    [pendingConfig],
  );
  const stagedEnabled =
    (getPending("step", step.id, "is_enabled")?.newValue as
      | boolean
      | undefined) ?? step.is_enabled;
  const stagedStage =
    (getPending("step", step.id, "stage")?.newValue as string | undefined) ??
    step.stage;
  const stagedOrder =
    (getPending("step", step.id, "stage_order")?.newValue as
      | number
      | undefined) ?? step.stage_order;
  const hasPending =
    !!getPending("step", step.id, "name") ||
    !!getPending("step", step.id, "config") ||
    !!getPending("step", step.id, "is_enabled") ||
    !!getPending("step", step.id, "stage") ||
    !!getPending("step", step.id, "stage_order");

  const openEdit = () => {
    setNameDraft(stagedName);
    setStageDraft(stagedStage);
    setOrderDraft(String(stagedOrder));
    setDraft(stagedConfig);
    setRawDraft(JSON.stringify(stagedConfig, null, 2));
    setRawError(null);
    setEditOpen(true);
  };

  const handleToggle = (enabled: boolean) =>
    stage({
      entityType: "step",
      entityId: step.id,
      section: "steps",
      entityLabel,
      field: "is_enabled",
      fieldLabel: "Enabled",
      valueKind: "enabled",
      oldValue: step.is_enabled,
      newValue: enabled,
    });

  const handleApply = () => {
    let config: Json;
    if (schema) {
      config = draft;
    } else {
      try {
        config = rawDraft.trim() ? JSON.parse(rawDraft) : {};
      } catch {
        setRawError("Invalid JSON.");
        return;
      }
    }
    if (!nameDraft.trim()) return;
    const base = {
      entityType: "step" as const,
      entityId: step.id,
      section: "steps" as const,
      entityLabel,
    };
    stage({
      ...base,
      field: "name",
      fieldLabel: "Name",
      valueKind: "text",
      oldValue: step.name,
      newValue: nameDraft.trim(),
    });
    stage({
      ...base,
      field: "config",
      fieldLabel: "Configuration",
      valueKind: "config",
      oldValue: (step.config as Json) ?? {},
      newValue: config,
      schema,
    });
    stage({
      ...base,
      field: "stage",
      fieldLabel: "Stage",
      valueKind: "text",
      oldValue: step.stage,
      newValue: stageDraft,
    });
    stage({
      ...base,
      field: "stage_order",
      fieldLabel: "Stage order",
      valueKind: "text",
      oldValue: step.stage_order,
      newValue: parseInt(orderDraft, 10),
    });
    setEditOpen(false);
  };

  const handleDelete = async () => {
    try {
      await deleteStep({ integrationId, id: step.id }).unwrap();
      dispatch(
        pushNotification({ severity: "success", message: "Step deleted." }),
      );
      setConfirmDelete(false);
    } catch (err) {
      const data = (err as { data?: unknown })?.data;
      setDeleteError(
        Array.isArray(data) ? data.join(" ") : "Could not delete the step.",
      );
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <StageChip stage={stagedStage} label={stageLabel} />
        <Chip
          size="small"
          variant="outlined"
          label={`Order ${stagedOrder}`}
          sx={{ fontFamily: "monospace" }}
        />
        <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
          {stagedName}
        </Typography>
        {hasPending && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label="Unsaved changes"
          />
        )}
        {canEdit && (
          <>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={stagedEnabled}
                  onChange={(_, c) => handleToggle(c)}
                />
              }
              label="Enabled"
              sx={{ mr: 0 }}
            />
            <Tooltip title="Edit step (name and config)">
              <IconButton
                size="small"
                onClick={openEdit}
                aria-label="Edit step"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {canManageStructure && (
          <Tooltip title="Delete step">
            <IconButton
              size="small"
              onClick={() => {
                setDeleteError(null);
                setConfirmDelete(true);
              }}
              aria-label="Delete step"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Typography
        variant="caption"
        sx={{
          fontFamily: "monospace",
          color: "text.secondary",
          wordBreak: "break-all",
        }}
      >
        {step.step_key}
      </Typography>

      <ConnectionChipRow
        step={step}
        catalogEntry={catalogEntry}
        onNavigate={onNavigateToConnection}
      />

      {issues.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {issues.map((issue, index) => (
            <Typography
              key={`${issue.code}-${index}`}
              variant="caption"
              color={issue.severity === "error" ? "error.main" : "warning.main"}
              display="block"
            >
              {issue.message}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 1 }}>
        <PayloadView
          value={stagedConfig}
          emptyHint="No configuration"
          boxed
          title="Configuration"
          copyable
          changedPaths={changedConfigPaths}
        />
      </Box>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Edit step — ${step.name}`}
        maxWidth="sm"
        showCloseIcon
        actions={
          <>
            <CustomButton fullWidth={false} onClick={() => setEditOpen(false)}>
              Cancel
            </CustomButton>
            <CustomButton
              fullWidth={false}
              onClick={handleApply}
              disabled={
                !nameDraft.trim() ||
                orderDraft === "" ||
                parseInt(orderDraft, 10) < 1
              }
            >
              Apply
            </CustomButton>
          </>
        }
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Name"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            helperText={
              "Display name of this step in THIS integration only — other " +
              "integrations using the same step are not affected."
            }
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              select
              size="small"
              label="Stage"
              value={stageDraft}
              onChange={(e) => setStageDraft(e.target.value)}
              disabled={!canManageStructure}
              sx={{ flex: 1 }}
            >
              {(stages ?? []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Order"
              inputMode="numeric"
              value={orderDraft}
              error={orderDraft !== "" && parseInt(orderDraft, 10) < 1}
              onChange={(e) =>
                setOrderDraft(e.target.value.replace(/[^0-9]/g, ""))
              }
              disabled={!canManageStructure}
              sx={{ width: 120 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Steps run stage by stage in ascending order; steps sharing the
            same order run in parallel. A step that records delivered files
            must have a HIGHER order than the delivery step.
            {!canManageStructure &&
              " Stage and order are pipeline structure - only a developer may change them."}
          </Typography>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Config
            </Typography>
            {schema ? (
              <JsonSchemaForm
                schema={schema}
                value={draft}
                onChange={setDraft}
              />
            ) : (
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Config (JSON)"
                value={rawDraft}
                error={!!rawError}
                helperText={
                  rawError ?? "No schema for this step — edit raw JSON."
                }
                onChange={(e) => {
                  setRawDraft(e.target.value);
                  setRawError(null);
                }}
                sx={{ fontFamily: "monospace" }}
              />
            )}
          </Box>
        </Stack>
      </AppDialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete step"
        message={
          <Box>
            <Typography variant="body2">
              Delete step "{step.name}"? Run history is kept; the step will no
              longer execute.
            </Typography>
            {deleteError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {deleteError}
              </Alert>
            )}
          </Box>
        }
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}
